import { WEBINAR_TIMEZONE } from "../constants";
import { SessionAccess, WebinarSession } from "../types/models";
import { todayYmdInTimezone } from "./deriveStartAt";

export type SessionFormState = {
  id?: string;
  title: string;
  oneLiner: string;
  fullDesc: string;
  takeaways: string[];
  topicId: string;
  hostId: string;
  date: string;
  time: string;
  mins: number;
  access: SessionAccess;
  cohorts: string[];
  lang: string;
  format: string;
  provider: string;
  joinUrl: string;
  recurrence?: string;
  recEnd: string;
  /** Original start when editing scheduled — used for rescheduled flag */
  origStart?: number;
  currentStatus?: WebinarSession["status"];
};

/** Add calendar days to an IST YYYY-MM-DD date string. */
function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const utc = Date.UTC(y, m - 1, d + days);
  const dt = new Date(utc);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function defaultDatePlusDays(days: number): string {
  return addDaysYmd(todayYmdInTimezone(WEBINAR_TIMEZONE), days);
}

export function blankSessionForm(): SessionFormState {
  return {
    title: "",
    oneLiner: "",
    fullDesc: "",
    takeaways: ["", "", ""],
    topicId: "",
    hostId: "",
    date: defaultDatePlusDays(5),
    time: "19:00",
    mins: 45,
    access: "open",
    cohorts: [],
    lang: "English",
    format: "Talk + Q&A",
    provider: "GoodFlip Live",
    joinUrl: "",
    recurrence: "none",
    recEnd: "",
  };
}

export function formFromSession(session: WebinarSession): SessionFormState {
  return {
    id: session.id,
    title: session.title,
    oneLiner: session.oneLiner,
    fullDesc: session.fullDesc,
    takeaways:
      session.takeaways.length > 0
        ? [...session.takeaways]
        : ["", "", ""],
    topicId: session.topicId,
    hostId: session.hostId,
    date: session.date,
    time: session.time,
    mins: session.mins,
    access: session.access,
    cohorts: [...session.cohorts],
    lang: session.lang,
    format: session.format,
    provider: session.provider,
    joinUrl: session.joinUrl,
    recurrence: session.recurrence,
    recEnd: session.recEnd || "",
    origStart: session.start,
    currentStatus: session.backendStatus ?? session.status,
  };
}

export function formForDuplicate(session: WebinarSession): SessionFormState {
  const blank = blankSessionForm();
  return {
    ...formFromSession(session),
    id: undefined,
    date: blank.date,
    time: blank.time,
    joinUrl: "",
    recurrence: "none",
    recEnd: "",
    origStart: undefined,
    currentStatus: "draft",
  };
}

export function formTimezone(): typeof WEBINAR_TIMEZONE {
  return WEBINAR_TIMEZONE;
}
